import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import BlogSection from '../components/BlogSection';
import BlogPreviewColumns from '../components/BlogPreviewColumns';
import BlogSidebar from '../components/BlogSidebar';
import {
  Heart,
  Eye,
  MessageCircle,
  Share2,
  Bookmark,
  UserPlus,
  UserMinus,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [viewTracked, setViewTracked] = useState(false);
  const [impressionTracked, setImpressionTracked] = useState(false);
  const [scrollDetected, setScrollDetected] = useState(false);
  const contentRef = useRef(null);
  const viewTimerRef = useRef(null);
  const impressionTimerRef = useRef(null);

  // Fetch blog
  const { data: blog, isLoading } = useQuery({
    queryKey: ['blog', slug],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.DETAIL(slug));
      return response.data;
    },
  });

  // Fetch related blogs (must be called before any early returns) - need 9 for columns
  const { data: relatedData } = useQuery({
    queryKey: ['blogs', 'related', blog?.category],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { category: blog.category, limit: 9 },
      });
      return response.data;
    },
    enabled: !!blog?.category,
  });

  // Track view after 3 seconds
  useEffect(() => {
    if (!blog || viewTracked) return;

    viewTimerRef.current = setTimeout(() => {
      trackView();
    }, 3000);

    return () => {
      if (viewTimerRef.current) {
        clearTimeout(viewTimerRef.current);
      }
    };
  }, [blog, viewTracked]);

  // Track impression after 30 seconds + scroll
  useEffect(() => {
    if (!blog || impressionTracked) return;

    const handleScroll = () => {
      setScrollDetected(true);
    };

    window.addEventListener('scroll', handleScroll);

    impressionTimerRef.current = setTimeout(() => {
      if (scrollDetected) {
        trackImpression();
      }
    }, 30000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (impressionTimerRef.current) {
        clearTimeout(impressionTimerRef.current);
      }
    };
  }, [blog, impressionTracked, scrollDetected]);

  const trackView = async () => {
    if (viewTracked || !blog) return;
    try {
      await api.post(API_ENDPOINTS.BLOGS.VIEW(blog._id));
      setViewTracked(true);
      // Update blog views in cache
      queryClient.setQueryData(['blog', slug], (old) => ({
        ...old,
        views: (old.views || 0) + 1,
      }));
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const trackImpression = async () => {
    if (impressionTracked || !blog) return;
    try {
      await api.post(API_ENDPOINTS.BLOGS.IMPRESSION(blog._id));
      setImpressionTracked(true);
      // Update blog impressions in cache
      queryClient.setQueryData(['blog', slug], (old) => ({
        ...old,
        impressions: (old.impressions || 0) + 1,
      }));
    } catch (error) {
      console.error('Error tracking impression:', error);
    }
  };

  // Like mutation
  const likeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(API_ENDPOINTS.BLOGS.LIKE(blog._id));
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['blog', slug], (old) => ({
        ...old,
        isLiked: data.isLiked,
        likes: data.likes,
      }));
    },
    onError: () => {
      toast.error('Failed to like blog');
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(API_ENDPOINTS.BLOGS.SAVE(blog._id));
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['blog', slug], (old) => ({
        ...old,
        isSaved: data.isSaved,
      }));
      toast.success(data.isSaved ? 'Blog saved!' : 'Blog unsaved');
    },
    onError: () => {
      toast.error('Failed to save blog');
    },
  });

  // Follow mutation
  const followMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(API_ENDPOINTS.BLOGS.FOLLOW(blog.author._id));
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['blog', slug], (old) => ({
        ...old,
        isFollowing: data.isFollowing,
      }));
      toast.success(data.isFollowing ? 'Following blogger' : 'Unfollowed blogger');
    },
    onError: () => {
      toast.error('Failed to follow blogger');
    },
  });

  // Comment mutation
  const [commentText, setCommentText] = useState('');
  const commentMutation = useMutation({
    mutationFn: async (content) => {
      const response = await api.post(API_ENDPOINTS.BLOGS.COMMENT(blog._id), {
        content,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['blog', slug], (old) => ({
        ...old,
        comments: [...(old.comments || []), data.comment],
      }));
      setCommentText('');
      toast.success('Comment added');
    },
    onError: () => {
      toast.error('Failed to add comment');
    },
  });

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          navigator.clipboard.writeText(url);
          toast.success('Link copied to clipboard');
        }
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-heading dark:text-white mb-4">
            Blog not found
          </h2>
          <Link to="/" className="text-accent hover:underline">
            Go back home
          </Link>
        </div>
      </div>
    );
  }

  const relatedBlogs = relatedData?.blogs?.filter((b) => b._id !== blog._id).slice(0, 9) || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-accent mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        {/* Main Layout: Content Left, Sidebar Right */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Main Content - Left (2/3) */}
          <div className="lg:col-span-2">
            {/* Thumbnail */}
            <div className="mb-6">
              <img
                src={blog.thumbnail}
                alt={blog.title}
                className="w-full h-96 object-cover rounded-lg"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/1200x600?text=Blog+Image';
                }}
              />
            </div>

            {/* Header */}
            <header className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-accent">{blog.category}</span>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => likeMutation.mutate()}
                disabled={!isAuthenticated}
                className={`flex items-center space-x-1 px-3 py-1 rounded-lg transition-colors ${
                  blog.isLiked
                    ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Heart className={`w-4 h-4 ${blog.isLiked ? 'fill-current' : ''}`} />
                <span>{blog.likes || 0}</span>
              </button>
              <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                <Eye className="w-4 h-4" />
                <span>{blog.views || 0}</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                <MessageCircle className="w-4 h-4" />
                <span>{blog.comments?.length || 0}</span>
              </div>
              <button
                onClick={handleShare}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-accent transition-colors"
              >
                <Share2 className="w-4 h-4" />
              </button>
              {isAuthenticated && (
                <button
                  onClick={() => saveMutation.mutate()}
                  className={`p-2 rounded-lg transition-colors ${
                    blog.isSaved
                      ? 'bg-accent text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Bookmark className={`w-4 h-4 ${blog.isSaved ? 'fill-current' : ''}`} />
                </button>
              )}
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-heading font-bold text-heading dark:text-white mb-4">
            {blog.title}
          </h1>

          {blog.excerpt && (
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">{blog.excerpt}</p>
          )}

          {/* Article Content */}
        <article ref={contentRef} className="prose prose-lg max-w-none mb-8">
          {blog.sections
            ?.sort((a, b) => a.order - b.order)
            .map((section, idx) => (
              <BlogSection key={idx} section={section} />
            ))}
        </article>

          {/* Action Button */}
          {blog.actionButton?.text && blog.actionButton?.link && (
            <div className="mb-6">
              <a
                href={blog.actionButton.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-accent text-white font-semibold rounded-lg hover:bg-accent/90 transition-colors"
              >
                {blog.actionButton.text}
              </a>
            </div>
          )}

          {/* Author Info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {blog.author?.profile?.avatar ? (
                <img
                  src={blog.author.profile.avatar}
                  alt={blog.author.username}
                  className="w-12 h-12 rounded-full"
                />
              ) : (
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white font-medium">
                  {blog.author?.username?.charAt(0).toUpperCase() || 'A'}
                </div>
              )}
              <div>
                <p className="font-medium text-heading dark:text-white">
                  {blog.author?.username || 'Anonymous'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {blog.publishedAt
                    ? formatDistanceToNow(new Date(blog.publishedAt), { addSuffix: true })
                    : formatDistanceToNow(new Date(blog.createdAt), { addSuffix: true })}
                </p>
              </div>
            </div>
            {isAuthenticated && blog.author?._id !== user?.id && (
              <button
                onClick={() => followMutation.mutate()}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  blog.isFollowing
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    : 'bg-accent text-white hover:bg-accent/90'
                }`}
              >
                {blog.isFollowing ? (
                  <>
                    <UserMinus className="w-4 h-4" />
                    <span>Unfollow</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span>Follow</span>
                  </>
                )}
              </button>
            )}
          </div>
        </header>

        

        {/* Comments Section - Inline under main content */}
        <section className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-heading dark:text-white mb-4">
            Comments ({blog.comments?.length || 0})
          </h2>

          {isAuthenticated && (
            <div className="mb-4">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Write a comment..."
                className="input-field w-full h-20 mb-2 text-sm"
              />
              <button
                onClick={() => commentMutation.mutate(commentText)}
                disabled={!commentText.trim() || commentMutation.isPending}
                className="btn-primary text-sm px-4 py-2"
              >
                {commentMutation.isPending ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          )}

          <div className="space-y-3">
            {blog.comments?.map((comment) => (
              <CommentItem key={comment._id} comment={comment} blogId={blog._id} />
            ))}
            {(!blog.comments || blog.comments.length === 0) && (
              <p className="text-gray-500 dark:text-gray-500 text-center py-6 text-sm">
                No comments yet. Be the first to comment!
              </p>
            )}
          </div>
        </section>
          </div>

          {/* Sidebar - Right (1/3) */}
          <div className="lg:col-span-1">
            <BlogSidebar excludeId={blog._id} category={blog.category} />
          </div>
        </div>

        {/* Related Blogs - 3 columns with 9 preview cards */}
        {relatedBlogs.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-heading dark:text-white mb-6">
              More from {blog.category}
            </h2>
            <BlogPreviewColumns blogs={relatedBlogs} />
            {relatedData?.pagination && relatedData.pagination.totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2 mt-8">
                <Link
                  to={`/category/${blog.category}`}
                  className="text-accent hover:text-accent/80 font-medium"
                >
                  View all {blog.category} stories →
                </Link>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

const CommentItem = ({ comment, blogId }) => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const { slug } = useParams();

  const likeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(
        API_ENDPOINTS.BLOGS.COMMENT_LIKE(blogId, comment._id)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['blog', slug]);
    },
  });

  const isLiked = comment.likedBy?.some((id) => id === currentUser?.id);

  return (
    <div className="card p-4">
      <div className="flex items-start space-x-3">
        {comment.user?.profile?.avatar ? (
          <img
            src={comment.user.profile.avatar}
            alt={comment.user.username}
            className="w-10 h-10 rounded-full"
          />
        ) : (
          <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center text-white text-sm font-medium">
            {comment.user?.username?.charAt(0).toUpperCase() || 'U'}
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <p className="font-medium text-heading dark:text-white">
              {comment.user?.username || 'Anonymous'}
            </p>
            <span className="text-xs text-gray-500 dark:text-gray-500">
              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
            </span>
          </div>
          <p className="text-default mb-2">{comment.content}</p>
          <button
            onClick={() => likeMutation.mutate()}
            disabled={!currentUser}
            className={`flex items-center space-x-1 text-xs ${
              isLiked ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-500'
            }`}
          >
            <Heart className={`w-3 h-3 ${isLiked ? 'fill-current' : ''}`} />
            <span>{comment.likes || 0}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogDetailPage;
